package top.wcpe.allincore.dsl.compiler.ast

import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * 顶层 `screen` 节点。
 */
data class ScreenAst(
    val id: String,
    val state: StateAst?,
    val root: ElementAst,
    /** DSL 文件头；未声明时按 legacy 处理。 */
    val dslHeader: DslHeaderAst? = null,
    /** v2 model 声明；legacy 文件为空。 */
    val model: ModelAst? = null,
    val theme: ThemeAst? = null,
    val animations: List<AnimationAst> = emptyList(),
    /**
     * session state 默认值；与 [state] 平级，但字段挂在跨 screen / hud 共享的 SharedStateRegistry 上。
     * 未声明 `session defaults { ... }` 时为 null。
     */
    val sessionState: StateAst? = null,
    /**
     * persist state 默认值；与 [state] 平级，但字段会被持久化到本地 JSON 文件。
     * 未声明 `persist defaults { ... }` 时为 null。
     */
    val persistState: StateAst? = null,
    /**
     * derived 派生值；与 [state] 平级，但条目值是表达式，运行时包成 Computed。
     * 未声明 `derived defaults { ... }` 时为 null。
     */
    val derived: DerivedAst? = null,
    /** screen 级函数列表（C4），支持 forward reference。 */
    val funs: List<FunDecl> = emptyList(),
    /**
     * screen 级 `onKey { event -> ... }` 事件 lambda；未声明时为 null。
     *
     * 当 UIRuntimeScreen 收到键盘事件、且未被焦点元素消费时，本 lambda 被调用；
     * lambda 体可访问 `event.key` / `event.scanCode` / `event.shift` 等字段，
     * 并可通过 `local.close()` 等内置 API 关闭界面。
     *
     * 仅派发 GLFW PRESS（action=1）；松开钩子请用 [onKeyReleaseLambda]，
     * 长按重复触发被平台层显式抑制以避免 GLFW REPEAT 风暴。
     */
    val onKeyLambda: ExprAst.LambdaExpr? = null,
    /**
     * screen 级 `onKeyRelease { event -> ... }` 松开事件 lambda；未声明时为 null。
     *
     * 与 [onKeyLambda] 严格对称,但只在玩家松开按键(GLFW RELEASE,action=0)时触发。
     * 用于实现"长按 N 秒松开触发"的交互,可通过 `event.holdMs` 拿到按下时长。
     *
     * 设计动机:历史实现把 RELEASE 也派发给 onKey lambda,导致诸如
     * `if (event.key == "E") { local.close() }` 在松开时被误触发,造成
     * "按 E 关背包后 vanilla 又重新打开"的循环 bug。新增独立钩子分离两种语义。
     */
    val onKeyReleaseLambda: ExprAst.LambdaExpr? = null,
    /**
     * RFC-INPUT-EVENT-UNIFICATION Stage 4：与 [onKeyLambda] 对称的 screen 顶层
     * `onMouseDown { event -> ... }` 全局鼠标按下监听 lambda；未声明时为 null。
     *
     * 当 UIRuntimeScreen 收到鼠标按下事件、且未被元素 onClick / 元素 hotkey /
     * SlotEventBridge 消费时，本 lambda 被调用；lambda 体可访问 `event.x` /
     * `event.y` / `event.button` 等字段，并可通过 `local.close()` 等内置 API 关闭界面。
     */
    val onMouseDownLambda: ExprAst.LambdaExpr? = null,
    /**
     * RFC-MOUSE-EXTENDED-EVENTS Stage A：与 [onMouseDownLambda] 对称的 screen 顶层
     * `onMouseUp { event -> ... }` 全局鼠标释放监听 lambda；未声明时为 null。
     *
     * 当 UIRuntimeScreen 收到鼠标释放事件、且平台默认释放逻辑处理完成后，本 lambda 被调用；
     * lambda 体可访问 `event.x` / `event.y` / `event.button` 等字段。
     */
    val onMouseUpLambda: ExprAst.LambdaExpr? = null,
    /**
     * RFC-MOUSE-EXTENDED-EVENTS Stage B：与 [onMouseDownLambda] 对称的 screen 顶层
     * `onMouseMove { event -> ... }` 全局鼠标移动监听 lambda；未声明时为 null。
     *
     * 当 UIRuntimeScreen 收到鼠标移动事件时，本 lambda 被调用；lambda 体可访问
     * `event.x` / `event.y`，其中 `event.button` 为空字符串、`event.buttonCode` 为 -1。
     */
    val onMouseMoveLambda: ExprAst.LambdaExpr? = null,
    /** M6-a → Stage 2：顶层类声明列表（当前仅 DATA kind），仅供编译期类型校验，不进 IR。 */
    val classDecls: List<ClassDecl> = emptyList(),
    /**
     * M6-d：内联 component 声明列表（出现在 `screen { component "name" {...} }` 内）。
     *
     * 编译时由 [top.wcpe.allincore.dsl.compiler.pipeline.ScreenWithComponentsPipeline]
     * 与跨文件 component 一并注册到 ComponentRegistry，作用域仅限当前 screen。
     */
    val inlineComponents: List<ComponentAst> = emptyList(),
    /**
     * Stage 1：screen 级 `open { ... }` 生命周期块的 stmt 列表；未声明时为 null。
     *
     * - body 是 suspend 块，可包含 `await net.request` / `delay` 以及调用 screen 级
     *   suspend fun（需配合 await）。
     * - 平台层在屏幕首帧渲染完毕后异步调度，仅在屏幕首次 mount 时调用一次。
     * - 重复声明在 ScreenParser 阶段直接拒绝（DSL_E646）。
     */
    val openBody: List<StmtAst>? = null,
    /**
     * Stage 1：screen 级 `close { ... }` 生命周期块的 stmt 列表；未声明时为 null。
     *
     * - body 是同步块，由 CloseBlockChecker 拒绝 await / delay / net.request /
     *   state 字段赋值 / local.openScreen / local.back / suspend fun 调用
     *   等可能阻塞或异步的语句（DSL_E640..E645）。
     * - 平台层在屏幕销毁那一帧同步执行，所有销毁路径都触发。
     * - 重复声明在 ScreenParser 阶段直接拒绝（DSL_E646）。
     */
    val closeBody: List<StmtAst>? = null,
    /**
     * screen 级 `show { ... }` 生命周期块；未声明时为 null。
     * 每次界面显示触发（首次打开 + 常驻每次重开）；语义同 open（异步，允许 await）。
     */
    val showBody: List<StmtAst>? = null,
    /**
     * screen 级 `hide { ... }` 生命周期块；未声明时为 null。
     * 每次界面隐藏触发（常驻收起）；语义同 open（异步）。
     */
    val hideBody: List<StmtAst>? = null,
    /** Stage4：screen 级 PAPI 占位符块；允许 parser 保留重复块，交由语义层统一诊断。 */
    val papiBlocks: List<PapiBlockAst> = emptyList(),
    /** Stage B：screen 级 subscribe 订阅块；允许 parser 保留重复块，交由语义层统一诊断。 */
    val subscribeBlocks: List<SubscribeBlockAst> = emptyList(),
    /**
     * Stage 11：顶层块种类，区分 `screen { ... }` 与 `hud { ... }`。
     *
     * 默认 [ScreenKind.SCREEN] 保持向后兼容；HUD 在 ScreenParser 阶段标注，
     * 不进入 IR，仅供编译入口分类使用（commit 2 的服务端 ScreenRegistry / HudRegistry）。
     */
    val kind: ScreenKind = ScreenKind.SCREEN,
    /**
     * Stage 11+：hud 顶层 `anchor = "..."` 声明；与 root 元素属性 `hudAnchor` 等价但优先级更高。
     *
     * 仅 [kind] == [ScreenKind.HUD] 时有意义；screen 块永远为 null。
     * 取值与 [top.wcpe.allincore.mod.fabric.v1_20_1.ui.hud.HudAnchorParser] 解析的字符串一致：
     * top-left / top-center / top-right / center-left / center / center-right /
     * bottom-left / bottom-center / bottom-right。
     */
    val hudAnchor: String? = null,
    /**
     * Stage 11+：hud 顶层 `layer = "..."` 声明；HUD 渲染层级。
     *
     * 仅 [kind] == [ScreenKind.HUD] 时有意义；screen 块永远为 null。
     * 合法值：
     * - `above_chat`（默认）：fabric `HudRenderCallback` TAIL，等同当前行为
     * - `below_chat`：聊天框之下，由 `MixinChatComponent` HEAD 调用 hook 绘制
     * - `below_vanilla`：vanilla HUD 渲染前，由 `MixinGui.render` HEAD 调用 hook 绘制
     * - `overlay`：保留给后续覆盖层使用，当前与 `above_chat` 等价
     *
     * 非法值在 ScreenParser 阶段直接抛 [top.wcpe.allincore.dsl.compiler.diag.DslError.E649_INVALID_HUD_LAYER]。
     */
    val hudLayer: String? = null,
    /**
     * Stage 11+：hud 顶层 `hide = [...]` 声明；与 `.manifest.yml` 等价但优先级更高。
     *
     * 仅 [kind] == [ScreenKind.HUD] 时有意义；screen 块永远为空 list。
     * token 闭集见 [top.wcpe.allincore.dsl.ir.model.hud.HudMetaDefaults.ALLOWED_HIDE_TOKENS]，
     * 非法值在 ScreenParser 阶段丢弃。
     */
    val hudHideTokens: List<String> = emptyList(),
    /** HUD 同层绘制顺序；数值越大越靠上。 */
    val hudZIndex: Int = 0,
    /** HUD 布局策略；未声明时由 emitter 使用 IR 默认值。 */
    val hudLayout: HudLayoutAst? = null,
    /** HUD 自动堆叠策略；未声明时由 emitter 使用 IR 默认值。 */
    val hudStack: HudStackAst? = null,
    /** HUD 与 screen 共存时的可见策略；未声明时由 emitter 使用 IR 默认值。 */
    val hudVisibility: HudVisibilityAst? = null,
    /** HUD 每帧 tick 块；未声明时不写 tick body。 */
    val hudTickBody: List<StmtAst>? = null,
    /**
     * 界面级默认字体声明，对应 screen 顶层 `font { ... }` 块；未声明时为 null。
     *
     * 编译期由 ScreenEmitter 转成 IR 的 [top.wcpe.allincore.dsl.ir.model.UiScreenFont]，
     * 运行时写入 RendererRegistry.pageFont，作用于本界面所有带文字控件。
     */
    val screenFont: FontStyleAst? = null,
    /** Phase 3：intercept 路由拦截块；未声明时不拦截任何原版界面。 */
    val intercept: InterceptAst? = null,
    /** Phase 4：HUD keybinding 按键绑定声明列表；未声明时不注册任何按键。 */
    val hudKeyBindings: List<KeyBindingAst> = emptyList(),
    /**
     * 编译期 style-class 样式层：screen 顶层 `styles { ... }` 块声明的样式规则列表；未声明时为空。
     *
     * 与 theme / root / state 平级，纯编译期产物：由
     * [top.wcpe.allincore.dsl.compiler.pipeline.StyleClassApplyPass] 按元素 styleClasses 命中合并，
     * 合并后即被清空，绝不进入 IR。
     */
    val styles: List<StyleRuleAst> = emptyList(),
    val modifierBundles: List<ModifierBundleAst> = emptyList(),
    /** `import "path" as Alias` 顶层声明列表（ADR-0038）；跨文件组件引用别名消歧用。 */
    val imports: List<ImportDecl> = emptyList(),
    override val position: SourcePosition,
) : AstNode

/** HUD `layout { ... }` 顶层块。 */
data class HudLayoutAst(
    val offsetX: Int = 0,
    val offsetY: Int = 0,
    val safeArea: Boolean = true,
    val avoidVanilla: Boolean = true,
)

/** HUD `stack { ... }` 顶层块。 */
data class HudStackAst(
    val group: String? = null,
    val direction: String = "down",
    val gap: Int = 4,
    val order: Int = 0,
)

/** HUD `visibility { ... }` 顶层块。 */
data class HudVisibilityAst(
    val whenScreen: String = "hide",
    val allow: List<String> = emptyList(),
    val deny: List<String> = emptyList(),
)

/**
 * `fun NAME(params) { body }` 函数声明（C4）。
 *
 * 可出现在三个位置：
 * - 顶层（screen 块内，与 state/animation/root/theme 平级）
 * - 局部块内（任意 `{ }` 内），由 [StmtAst.FunDeclStmt] 包裹
 *
 * M5-b：新增 [defaults] 支持默认实参。列表与 [params] 等长，无默认值的位置为 null。
 */
data class FunDecl(
    val name: String,
    val params: List<String>,
    val body: List<StmtAst>,
    override val position: SourcePosition,
    /** M5-b：各形参的默认值表达式；与 [params] 等长，无默认值为 null。空列表表示全部无默认值。 */
    val defaults: List<ExprAst?> = emptyList(),
    /** M5-c：各形参的类型标注；与 [params] 等长，无标注为 null。空列表表示全部未标注。 */
    val paramTypes: List<TypeAst?> = emptyList(),
    /** M5-c：返回类型标注；未标注为 null。 */
    val returnType: TypeAst? = null,
    /** M5-e：是否为 suspend fun（用户显式声明 `suspend fun`）。 */
    val isSuspend: Boolean = false,
) : AstNode

/**
 * M5-b：命名实参 `name = value`。
 *
 * 出现在 [ExprAst.CallExpr.namedArgs] 中。
 */
data class NamedArg(
    val name: String,
    val value: ExprAst,
    val position: SourcePosition,
)

/** data 字段：名 + 类型 + 可选默认值。 */
data class DataField(
    val name: String,
    val type: TypeAst,
    val default: ExprAst?,
    val position: SourcePosition,
)

/** `import "path" as Alias` 顶层声明（ADR-0038）：把某组件路径绑定为本文件局部别名。 */
data class ImportDecl(
    val path: String,
    val alias: String,
    val position: SourcePosition,
)
